import { msAPI } from "./index.js";

/**
 * Main Menu Cards admin API (MenuService).
 *
 * These endpoints are being built on the backend. The exact paths are isolated
 * here so they're trivial to adjust once the endpoints land. A "region" is the
 * unit returned/persisted:
 *
 *   {
 *     id,                                   // server id (absent until first save)
 *     name,                                 // "West", "Texas", ...
 *     cities: ["los-angeles", ...],         // city ids assigned to this region
 *     web_images:    [ { image, link }, ... ],  // ordered web cards
 *     menu_images: [ { image, link }, ... ],  // ordered mobile cards
 *   }
 */

/**
 * Available cities for assignment to regions.
 * @returns {Promise<Array<{id: string, name: string, state?: string}>|Array<string>>}
 */
export const getCities = async () => (await msAPI.get("merchant/cities")).data;

/**
 * All menu-card regions.
 * @returns {Promise<Array<Object>>} array of region objects (see shape above)
 */
export const getMenuCards = async () => (await msAPI.get("merchant/regions")).data;

/**
 * Persist the full set of regions.
 * @param {Array<Object>} regions - region objects in the shape above
 * @returns {Promise<Object>}
 */
export const saveMenuCards = async (regions) =>
  (await msAPI.post("merchant/regions", { regions })).data;

/**
 * Upload a menu-card image. The backend stores it and returns the hosted URL.
 * @param {File} file - the image file (validated to 960x640 before calling)
 * @returns {Promise<string>} the hosted image URL
 */
export const uploadMenuCardImage = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const body = (
    await msAPI.post("merchant/menu-card-image", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    })
  ).data;
  // Tolerate a bare string URL, { url }/{ image }, or a wrapped envelope.
  const data = body?.data ?? body;
  return typeof data === "string" ? data : data?.url ?? data?.image;
};
